/**
 * Agora RTC React SDK Context
 *
 * This module provides video conferencing using the official agora-rtc-react SDK (v2.5.1).
 * It wraps the SDK's hooks and components to provide a VideoSDK-compatible interface.
 *
 * Key features:
 * - Uses AgoraRTCProvider for client management
 * - Leverages official React hooks (useJoin, useLocalMicrophoneTrack, etc.)
 * - Maintains backward compatibility with existing VideoSDK-style API
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AgoraRTC, {
  AgoraRTCProvider,
  useRTCClient,
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  usePublish,
  useRemoteUsers,
  useRemoteAudioTracks,
  useRemoteVideoTracks,
  useIsConnected,
  useConnectionState,
  LocalUser,
  RemoteUser,
} from 'agora-rtc-react';
import axios from 'axios';
import VirtualBackgroundExtension from 'agora-extension-virtual-background';

console.log('[AgoraReactContext] Module loaded - using agora-rtc-react SDK v2.5.1');

// Set log level
AgoraRTC.setLogLevel(1); // 0: DEBUG, 1: INFO, 2: WARNING, 3: ERROR, 4: NONE

// Force AudioContext to start without a user-gesture requirement. Required
// for headless Chrome on Agora's web_recorder_service bots — without this,
// remote audioTrack.play() calls succeed but produce SILENCE for ~27-30s
// until something else unsuspends the AudioContext. Result: recordings have
// no audible audio in the first ~30 seconds even though the AAC track is
// technically continuous (rec 69f4d7e6 confirmed this exact symptom).
//
// Safe for real users too: their AudioContext can autoplay after first user
// gesture anyway, so this parameter is a no-op for them. For recorder bots
// it's load-bearing — it tells AgoraRTC to attempt AudioContext.resume()
// proactively without waiting for user input.
AgoraRTC.setParameter('AUDIO_CONTEXT_AUTOPLAY_ENABLED', true);

// Recorder bots running on Agora's web_recorder_service never enable virtual
// backgrounds or AI denoising — the bot subscribes to already-published audio
// and never publishes its own camera. Skipping the VB extension registration
// when the URL signals recorder mode saves ~300KB of JS download + parse on
// the bot's Chrome, which directly cuts startup latency.
//
// Triggers (broadened 2026-05-01 to cover ALL recorder paths, not just lite):
//   - ?recorder=true query param (full meeting page recorder mode used by
//     both single-layout web AND the multi-layout-web fan-out)
//   - ?recorderLite=1 query param (older signal for the lite-v2 page)
//   - /recording-view-* path prefixes (covers /recording-view, /recording-view-lite,
//     /recording-view-lite-v2, /recording-view-lite-v3 etc.)
const isRecorderLite = (typeof window !== 'undefined') && (
  (new URLSearchParams(window.location.search).get('recorder') === 'true') ||
  (new URLSearchParams(window.location.search).get('recorderLite') === '1') ||
  window.location.pathname.startsWith('/recording-view')
);

// Register Agora Virtual Background extension (must be before client creation).
// Skipped entirely in lite-recorder mode — recorder bots don't apply it.
export const vbExtension = isRecorderLite ? null : new VirtualBackgroundExtension();
if (vbExtension && vbExtension.checkCompatibility()) {
  AgoraRTC.registerExtensions([vbExtension]);
  console.log('[AgoraReactContext] Virtual background extension registered');
} else if (isRecorderLite) {
  console.log('[AgoraReactContext] Lite recorder mode — VB extension skipped');
} else {
  console.warn('[AgoraReactContext] Virtual background extension not supported in this browser');
}

// Context for additional meeting state
const AgoraMeetingContext = createContext(null);

// Constants to mirror VideoSDK Constants
export const AgoraConstants = {
  modes: {
    SEND_AND_RECV: 'SEND_AND_RECV',
    RECV_ONLY: 'RECV_ONLY',
  },
  meetingState: {
    CONNECTING: 'CONNECTING',
    CONNECTED: 'CONNECTED',
    DISCONNECTED: 'DISCONNECTED',
    FAILED: 'FAILED',
    RECONNECTING: 'RECONNECTING',
  },
  participantModes: {
    CONFERENCE: 'CONFERENCE',
    VIEWER: 'VIEWER',
  },
  permission: {
    AUDIO: 'audio',
    VIDEO: 'video',
  },
  recordingEvents: {
    RECORDING_STARTING: 'RECORDING_STARTING',
    RECORDING_STARTED: 'RECORDING_STARTED',
    RECORDING_STOPPING: 'RECORDING_STOPPING',
    RECORDING_STOPPED: 'RECORDING_STOPPED',
  },
  transcriptionEvents: {
    TRANSCRIPTION_STARTING: 'TRANSCRIPTION_STARTING',
    TRANSCRIPTION_STARTED: 'TRANSCRIPTION_STARTED',
    TRANSCRIPTION_STOPPING: 'TRANSCRIPTION_STOPPING',
    TRANSCRIPTION_STOPPED: 'TRANSCRIPTION_STOPPED',
  },
  translationEvents: {
    TRANSLATION_STARTING: 'TRANSLATION_STARTING',
    TRANSLATION_STARTED: 'TRANSLATION_STARTED',
    TRANSLATION_STOPPING: 'TRANSLATION_STOPPING',
    TRANSLATION_STOPPED: 'TRANSLATION_STOPPED',
  },
  hlsEvents: {
    HLS_STARTING: 'HLS_STARTING',
    HLS_STARTED: 'HLS_STARTED',
    HLS_PLAYABLE: 'HLS_PLAYABLE',
    HLS_STOPPING: 'HLS_STOPPING',
    HLS_STOPPED: 'HLS_STOPPED',
  },
};

// Utility to fetch token from backend
async function getAgoraToken(channelName, uid, role = 'publisher') {
  console.log('[getAgoraToken] Requesting token for channel:', channelName, 'uid:', uid);
  try {
    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/rtc-token`, {
      channelName,
      uid,
      role
    });
    console.log('[getAgoraToken] Token received successfully');
    return response.data.token;
  } catch (error) {
    console.error('[getAgoraToken] Failed to get Agora token:', error);
    return null;
  }
}

// Helper function to convert string ID to numeric UID for Agora
export function stringToNumericUid(str) {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// Screen share UID is the main UID + 1
function getScreenShareUid(mainUid) {
  return mainUid + 1;
}

function getMainUidFromScreenShare(screenShareUid) {
  return screenShareUid - 1;
}

/**
 * Inner Meeting Provider Component
 * This component uses the agora-rtc-react hooks inside the AgoraRTCProvider
 */
function AgoraMeetingInner({
  children,
  config,
  token: externalToken,
  joinWithoutUserInteraction = false,
}) {
  const {
    meetingId,
    participantId,
    name: participantName,
    micEnabled = false,
    webcamEnabled = false,
    mode = AgoraConstants.modes.SEND_AND_RECV,
    metaData = {},
  } = config || {};

  const appId = process.env.REACT_APP_AGORA_APP_ID;
  const numericUid = stringToNumericUid(participantId);

  // Get the RTC client from context
  const client = useRTCClient();

  // State for token and joining
  const [token, setToken] = useState(externalToken || null);
  const [shouldJoin, setShouldJoin] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [presenterId, setPresenterId] = useState(null);
  const [pinnedParticipants, setPinnedParticipants] = useState(new Map());
  const [remoteUserMetadata, setRemoteUserMetadata] = useState(new Map());
  const [localScreenTrack, setLocalScreenTrack] = useState(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);

  // Refs
  const callbacksRef = useRef({});
  const screenClientRef = useRef(null);
  const screenClientJoinedRef = useRef(false);

  // Track enabled states
  const [isMicOn, setIsMicOn] = useState(micEnabled);
  const [isWebcamOn, setIsWebcamOn] = useState(webcamEnabled);

  // Create local tracks using SDK hooks - UNCONDITIONALLY per official SDK pattern
  // The SDK handles track lifecycle; we just control enabled state
  const isNotRecvOnly = mode !== AgoraConstants.modes.RECV_ONLY;
  const { localMicrophoneTrack, isLoading: isMicLoading } = useLocalMicrophoneTrack(isNotRecvOnly);
  const { localCameraTrack, isLoading: isCamLoading } = useLocalCameraTrack(isNotRecvOnly);

  console.log('[AgoraReactContext] Track hooks:', {
    localMicrophoneTrack: !!localMicrophoneTrack,
    localCameraTrack: !!localCameraTrack,
    isMicLoading,
    isCamLoading,
    isNotRecvOnly
  });

  // Track if we've done initial setup
  const initialSetupDoneRef = useRef(false);

  // Fetch token on mount
  useEffect(() => {
    console.log('[AgoraReactContext] Token fetch check:', {
      hasExternalToken: !!externalToken,
      meetingId,
      numericUid,
      currentToken: !!token
    });

    if (externalToken) {
      console.log('[AgoraReactContext] Using external token');
      setToken(externalToken);
    } else if (meetingId && numericUid) {
      console.log('[AgoraReactContext] Fetching token from backend...');
      getAgoraToken(meetingId, numericUid).then(fetchedToken => {
        if (fetchedToken) {
          console.log('[AgoraReactContext] Token fetched successfully');
          setToken(fetchedToken);
        } else {
          console.error('[AgoraReactContext] Failed to fetch token');
        }
      });
    }
  }, [meetingId, numericUid, externalToken]);

  // DEBUG: Log all join parameters before calling useJoin
  const joinReady = shouldJoin && !!token && !!meetingId;
  console.log('[AgoraReactContext] ====== useJoin PARAMETERS ======');
  console.log('[AgoraReactContext] appId:', appId);
  console.log('[AgoraReactContext] channel (meetingId):', meetingId);
  console.log('[AgoraReactContext] token:', token ? `${token.substring(0, 20)}...` : 'NULL');
  console.log('[AgoraReactContext] uid:', numericUid);
  console.log('[AgoraReactContext] ready flag:', joinReady);
  console.log('[AgoraReactContext] shouldJoin:', shouldJoin);
  console.log('[AgoraReactContext] !!token:', !!token);
  console.log('[AgoraReactContext] !!meetingId:', !!meetingId);
  console.log('[AgoraReactContext] ================================');

  // Join the channel using SDK hook
  // useJoin returns { data, isLoading, isConnected, error }
  const joinResult = useJoin(
    {
      appid: appId,
      channel: meetingId || '',
      token: token,
      uid: numericUid,
    },
    joinReady
  );

  // DEBUG: Log raw joinResult
  console.log('[AgoraReactContext] ====== useJoin RESULT ======');
  console.log('[AgoraReactContext] Raw joinResult:', joinResult);
  console.log('[AgoraReactContext] joinResult type:', typeof joinResult);
  console.log('[AgoraReactContext] joinResult keys:', joinResult ? Object.keys(joinResult) : 'N/A');

  // Destructure with defaults
  const {
    data: joinData,
    isLoading: isJoining = false,
    isConnected: joinIsConnected = false,
    error: joinError = null
  } = joinResult || {};

  console.log('[AgoraReactContext] Destructured - data:', joinData, 'isLoading:', isJoining, 'isConnected:', joinIsConnected, 'error:', joinError);
  console.log('[AgoraReactContext] ============================');

  // Get connection state from dedicated hooks
  const connectionState = useConnectionState();
  const hookIsConnected = useIsConnected();

  // FIX: useIsConnected() and joinIsConnected can return stale false values
  // even when connectionState shows 'CONNECTED'. Use connectionState as primary source.
  const isConnected = connectionState === 'CONNECTED' || joinIsConnected || hookIsConnected;

  console.log('[AgoraReactContext] ====== CONNECTION STATE ======');
  console.log('[AgoraReactContext] connectionState hook:', connectionState);
  console.log('[AgoraReactContext] connectionState === CONNECTED:', connectionState === 'CONNECTED');
  console.log('[AgoraReactContext] useIsConnected hook:', hookIsConnected);
  console.log('[AgoraReactContext] joinIsConnected:', joinIsConnected);
  console.log('[AgoraReactContext] FINAL isConnected:', isConnected);
  console.log('[AgoraReactContext] ==============================');

  // Debug logging for connection state
  useEffect(() => {
    console.log('[AgoraReactContext] Connection debug:', {
      shouldJoin,
      token: !!token,
      meetingId,
      isJoining,
      isConnected,
      connectionState,
      joinError: joinError?.message || null,
      appId: !!appId,
      numericUid
    });
  }, [shouldJoin, token, meetingId, isJoining, isConnected, connectionState, joinError, appId, numericUid]);

  // Map connection state to meeting state
  // IMPORTANT: Check isConnected BEFORE isJoining to avoid getting stuck in CONNECTING
  const meetingState = useMemo(() => {
    console.log('[AgoraReactContext] Computing meetingState:', {
      shouldJoin,
      isJoining,
      isConnected,
      connectionState,
      joinError: !!joinError
    });

    if (!shouldJoin) return AgoraConstants.meetingState.DISCONNECTED;
    if (joinError) return AgoraConstants.meetingState.FAILED;
    // Check isConnected first - this is the most reliable indicator from useJoin
    if (isConnected) return AgoraConstants.meetingState.CONNECTED;
    if (connectionState === 'CONNECTED') return AgoraConstants.meetingState.CONNECTED;
    if (connectionState === 'RECONNECTING') return AgoraConstants.meetingState.RECONNECTING;
    if (isJoining) return AgoraConstants.meetingState.CONNECTING;
    // If shouldJoin is true but nothing else, we're in connecting state
    return AgoraConstants.meetingState.CONNECTING;
  }, [shouldJoin, isJoining, isConnected, connectionState, joinError]);

  // Publish local tracks - following official SDK pattern
  // The SDK handles null/undefined tracks gracefully
  // We pass tracks directly and let the SDK manage publishing
  const tracksToPublish = useMemo(() => {
    const tracks = [];
    if (localMicrophoneTrack) tracks.push(localMicrophoneTrack);
    if (localCameraTrack) tracks.push(localCameraTrack);
    return tracks;
  }, [localMicrophoneTrack, localCameraTrack]);

  console.log('[AgoraReactContext] usePublish - tracks:', tracksToPublish.length, 'isConnected:', isConnected);

  // Per official pattern: usePublish is called unconditionally
  // The SDK auto-publishes when connected and unpublishes when disconnected
  usePublish(tracksToPublish);

  // Get remote users using SDK hooks
  const remoteUsers = useRemoteUsers();
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  const { videoTracks } = useRemoteVideoTracks(remoteUsers);

  // Play remote audio tracks
  useEffect(() => {
    audioTracks.forEach(track => {
      if (track && !track.isPlaying) {
        track.play();
      }
    });
  }, [audioTracks]);

  // Enable dual-stream when connected
  useEffect(() => {
    if (isConnected && client) {
      client.enableDualStream().catch(err => {
        console.warn('[AgoraReactContext] Failed to enable dual-stream:', err);
      });
    }
  }, [isConnected, client]);

  // Sync camera track enabled state when track becomes available
  useEffect(() => {
    if (localCameraTrack) {
      console.log('[AgoraReactContext] Camera track available, setting enabled:', isWebcamOn);
      localCameraTrack.setEnabled(isWebcamOn).catch(err => {
        console.warn('[AgoraReactContext] Failed to set camera enabled state:', err);
      });
    }
  }, [localCameraTrack, isWebcamOn]);

  // Sync microphone track enabled state when track becomes available
  useEffect(() => {
    if (localMicrophoneTrack) {
      console.log('[AgoraReactContext] Microphone track available, setting enabled:', isMicOn);
      localMicrophoneTrack.setEnabled(isMicOn).catch(err => {
        console.warn('[AgoraReactContext] Failed to set microphone enabled state:', err);
      });
    }
  }, [localMicrophoneTrack, isMicOn]);

  // Log track creation status for debugging
  useEffect(() => {
    console.log('[AgoraReactContext] Track status - Camera:', {
      trackExists: !!localCameraTrack,
      isLoading: isCamLoading,
      isWebcamOn,
      isConnected
    });
  }, [localCameraTrack, isCamLoading, isWebcamOn, isConnected]);

  useEffect(() => {
    console.log('[AgoraReactContext] Track status - Microphone:', {
      trackExists: !!localMicrophoneTrack,
      isLoading: isMicLoading,
      isMicOn,
      isConnected
    });
  }, [localMicrophoneTrack, isMicLoading, isMicOn, isConnected]);

  // Auto-join if configured
  useEffect(() => {
    console.log('[AgoraReactContext] Auto-join check:', {
      joinWithoutUserInteraction,
      hasToken: !!token,
      meetingId,
      shouldJoin
    });
    if (joinWithoutUserInteraction && token && meetingId && !shouldJoin) {
      console.log('[AgoraReactContext] Auto-join triggered - setting shouldJoin to true');
      setShouldJoin(true);
    }
  }, [joinWithoutUserInteraction, token, meetingId, shouldJoin]);

  // Notify callbacks on state changes
  useEffect(() => {
    console.log('[AgoraReactContext] Meeting state changed to:', meetingState);
    callbacksRef.current.onMeetingStateChanged?.({ state: meetingState });
    if (meetingState === AgoraConstants.meetingState.CONNECTED) {
      console.log('[AgoraReactContext] Meeting connected - calling onMeetingJoined callback');
      callbacksRef.current.onMeetingJoined?.();
    }
  }, [meetingState]);

  // Join action
  const join = useCallback(() => {
    console.log('[AgoraReactContext] join() called');
    setShouldJoin(true);
  }, []);

  // Leave action
  const leave = useCallback(async () => {
    console.log('[AgoraReactContext] leave() called');
    setShouldJoin(false);

    // Clean up screen share
    if (localScreenTrack) {
      localScreenTrack.stop();
      localScreenTrack.close();
      setLocalScreenTrack(null);
    }
    if (screenClientJoinedRef.current && screenClientRef.current) {
      await screenClientRef.current.leave();
      screenClientJoinedRef.current = false;
    }
    setIsScreenSharing(false);
    setPresenterId(null);

    callbacksRef.current.onMeetingLeft?.();
  }, [localScreenTrack]);

  // End meeting
  const end = useCallback(async () => {
    await leave();
  }, [leave]);

  // Mic controls - tracks are always created, we just toggle enabled state
  const muteMic = useCallback(async () => {
    console.log('[AgoraReactContext] muteMic called, localMicrophoneTrack:', !!localMicrophoneTrack);
    if (localMicrophoneTrack) {
      await localMicrophoneTrack.setEnabled(false);
    }
    setIsMicOn(false);
  }, [localMicrophoneTrack]);

  const unmuteMic = useCallback(async () => {
    console.log('[AgoraReactContext] unmuteMic called, localMicrophoneTrack:', !!localMicrophoneTrack);
    setIsMicOn(true);
    if (localMicrophoneTrack) {
      await localMicrophoneTrack.setEnabled(true);
    }
  }, [localMicrophoneTrack]);

  const toggleMic = useCallback(async () => {
    if (isMicOn) {
      await muteMic();
    } else {
      await unmuteMic();
    }
  }, [isMicOn, muteMic, unmuteMic]);

  // Webcam controls - tracks are always created, we just toggle enabled state
  const disableWebcam = useCallback(async () => {
    console.log('[AgoraReactContext] disableWebcam called, localCameraTrack:', !!localCameraTrack);
    if (localCameraTrack) {
      await localCameraTrack.setEnabled(false);
    }
    setIsWebcamOn(false);
  }, [localCameraTrack]);

  const enableWebcam = useCallback(async () => {
    console.log('[AgoraReactContext] enableWebcam called, localCameraTrack:', !!localCameraTrack);
    setIsWebcamOn(true);
    if (localCameraTrack) {
      await localCameraTrack.setEnabled(true);
    }
  }, [localCameraTrack]);

  const toggleWebcam = useCallback(async () => {
    if (isWebcamOn) {
      await disableWebcam();
    } else {
      await enableWebcam();
    }
  }, [isWebcamOn, disableWebcam, enableWebcam]);

  // Change webcam (for virtual backgrounds)
  const changeWebcam = useCallback(async (newStream) => {
    console.log('[AgoraReactContext] changeWebcam called');
    // The SDK handles track replacement internally
    // For virtual backgrounds, we may need to create a custom track
  }, []);

  // Screen sharing
  const enableScreenShare = useCallback(async () => {
    try {
      // Create screen client if not exists
      if (!screenClientRef.current) {
        screenClientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      }

      // Create screen tracks
      const screenTracks = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: '1080p_2'
      }, 'auto');

      let videoTrack, audioTrack;
      if (Array.isArray(screenTracks)) {
        [videoTrack, audioTrack] = screenTracks;
      } else {
        videoTrack = screenTracks;
      }

      setLocalScreenTrack(videoTrack);

      // Use separate UID for screen share
      const screenShareUid = getScreenShareUid(numericUid);
      const screenToken = await getAgoraToken(meetingId, screenShareUid);

      // Join channel with screen client
      await screenClientRef.current.join(appId, meetingId, screenToken, screenShareUid);
      screenClientJoinedRef.current = true;

      // Publish screen track
      await screenClientRef.current.publish(audioTrack ? [videoTrack, audioTrack] : videoTrack);
      setIsScreenSharing(true);
      setPresenterId(participantId);
      callbacksRef.current.onPresenterChanged?.(participantId);

      // Notify backend
      try {
        await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/screen-share/start`, {
          meetingId,
          participantId: String(screenShareUid),
          mainParticipantId: String(numericUid),
        });
      } catch (err) {
        console.error('Failed to notify screen share start:', err);
      }

      // Handle browser stop share button
      videoTrack.on('track-ended', () => {
        disableScreenShare();
      });
    } catch (error) {
      console.error('Failed to enable screen share:', error);
    }
  }, [appId, meetingId, numericUid, participantId]);

  const disableScreenShare = useCallback(async () => {
    try {
      if (localScreenTrack) {
        localScreenTrack.stop();
        localScreenTrack.close();
        setLocalScreenTrack(null);
      }

      if (screenClientJoinedRef.current && screenClientRef.current) {
        await screenClientRef.current.leave();
        screenClientJoinedRef.current = false;
      }

      setIsScreenSharing(false);
      setPresenterId(null);
      callbacksRef.current.onPresenterChanged?.(null);

      // Notify backend
      try {
        const screenShareUid = getScreenShareUid(numericUid);
        await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/screen-share/stop`, {
          meetingId,
          participantId: String(screenShareUid),
        });
      } catch (err) {
        console.error('Failed to notify screen share stop:', err);
      }
    } catch (error) {
      console.error('Failed to disable screen share:', error);
    }
  }, [localScreenTrack, meetingId, numericUid]);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      await disableScreenShare();
    } else {
      await enableScreenShare();
    }
  }, [isScreenSharing, enableScreenShare, disableScreenShare]);

  // Pin/unpin participants
  const pin = useCallback((participantIdToPin) => {
    setPinnedParticipants(prev => {
      const newMap = new Map(prev);
      newMap.set(participantIdToPin, { cam: true });
      return newMap;
    });
  }, []);

  const unpin = useCallback((participantIdToUnpin) => {
    setPinnedParticipants(prev => {
      const newMap = new Map(prev);
      newMap.delete(participantIdToUnpin);
      return newMap;
    });
  }, []);

  // Sync participant info via backend API
  useEffect(() => {
    let pollInterval = null;

    const fetchParticipantInfo = async () => {
      if (!meetingId) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/agora/participant-info/${meetingId}`
        );
        const { participants: participantsData } = response.data;

        if (participantsData) {
          setRemoteUserMetadata(prev => {
            const newMap = new Map(prev);
            const ourNumericUid = String(numericUid);
            Object.entries(participantsData).forEach(([id, info]) => {
              if (id !== participantId && id !== 'local' && id !== ourNumericUid) {
                newMap.set(id, info);
              }
            });
            return newMap;
          });
        }
      } catch (error) {
        console.error('Error fetching participant info:', error);
      }
    };

    const registerParticipantInfo = async () => {
      if (!meetingId || !participantId) return;

      try {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/agora/participant-info`,
          {
            meetingId,
            participantId: String(numericUid),
            displayName: participantName,
            metaData: metaData,
          }
        );
        await fetchParticipantInfo();
      } catch (error) {
        console.error('Error registering participant info:', error);
      }
    };

    // Re-register our info periodically to survive backend restarts
    let reRegisterCount = 0;
    const pollWithReRegister = async () => {
      reRegisterCount++;
      // Re-register every 5th poll cycle (~15s) to keep our name in backend
      if (reRegisterCount % 5 === 0) {
        registerParticipantInfo();
      } else {
        fetchParticipantInfo();
      }
    };

    if (meetingState === AgoraConstants.meetingState.CONNECTED && meetingId && participantId) {
      registerParticipantInfo();
      pollInterval = setInterval(pollWithReRegister, 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [meetingState, meetingId, participantId, participantName, numericUid, metaData]);

  // Build participants map (VideoSDK-compatible format)
  const participants = useMemo(() => {
    const allParticipants = new Map();

    // Add local participant
    if (meetingState === AgoraConstants.meetingState.CONNECTED || meetingState === AgoraConstants.meetingState.RECONNECTING) {
      const localVideoMediaTrack = localCameraTrack?.getMediaStreamTrack?.() || null;
      const localAudioMediaTrack = localMicrophoneTrack?.getMediaStreamTrack?.() || null;
      const localScreenMediaTrack = localScreenTrack?.getMediaStreamTrack?.() || null;

      const localParticipantData = {
        id: participantId || 'local',
        displayName: participantName || 'You',
        isLocal: true,
        mode: mode,
        webcamOn: isWebcamOn && !!localCameraTrack,
        micOn: isMicOn && !!localMicrophoneTrack,
        screenShareOn: isScreenSharing,
        webcamStream: localCameraTrack ? {
          track: localVideoMediaTrack,
          agoraTrack: localCameraTrack,
        } : null,
        micStream: localMicrophoneTrack ? {
          track: localAudioMediaTrack,
          agoraTrack: localMicrophoneTrack,
        } : null,
        screenShareStream: localScreenTrack ? {
          track: localScreenMediaTrack,
          agoraTrack: localScreenTrack,
        } : null,
        metaData: metaData,
        pinState: pinnedParticipants.has(participantId || 'local'),
      };

      allParticipants.set(participantId || 'local', localParticipantData);
    }

    // Add remote participants from SDK hook
    remoteUsers.forEach((user) => {
      const uid = user.uid;
      const userMetadata = remoteUserMetadata.get(uid) || remoteUserMetadata.get(String(uid));
      const displayName = userMetadata?.displayName || `Participant ${uid}`;

      const videoMediaTrack = user.videoTrack?.getMediaStreamTrack?.() || null;
      const audioMediaTrack = user.audioTrack?.getMediaStreamTrack?.() || null;

      allParticipants.set(uid, {
        id: uid,
        displayName: displayName,
        isLocal: false,
        mode: AgoraConstants.modes.SEND_AND_RECV,
        webcamOn: !!user.videoTrack,
        micOn: !!user.audioTrack,
        screenShareOn: false,
        webcamStream: user.videoTrack ? {
          track: videoMediaTrack,
          agoraTrack: user.videoTrack,
        } : null,
        micStream: user.audioTrack ? {
          track: audioMediaTrack,
          agoraTrack: user.audioTrack,
        } : null,
        screenShareStream: null,
        metaData: userMetadata?.metaData || {},
        pinState: pinnedParticipants.has(uid),
      });
    });

    return allParticipants;
  }, [meetingState, remoteUsers, remoteUserMetadata, participantId, participantName, isWebcamOn, isMicOn, isScreenSharing, localCameraTrack, localMicrophoneTrack, localScreenTrack, mode, metaData, pinnedParticipants]);

  const localParticipant = participants.get(participantId || 'local');

  // Set remote video stream type for simulcasting
  const setRemoteVideoStreamType = useCallback(async (uid, streamType) => {
    try {
      await client.setRemoteVideoStreamType(uid, streamType);
      console.log(`[AgoraReactContext] Set stream type for ${uid} to ${streamType === 0 ? 'HIGH' : 'LOW'}`);
    } catch (err) {
      console.warn(`[AgoraReactContext] Failed to set stream type for ${uid}:`, err);
    }
  }, [client]);

  // Meeting object (VideoSDK-compatible interface)
  const mMeeting = useMemo(() => ({
    meetingId,
    localParticipant,
    participants,
    presenterId,
    activeSpeakerId,
    pinnedParticipants,
    isRecording: false,
    recordingState: AgoraConstants.recordingEvents.RECORDING_STOPPED,
    hlsState: AgoraConstants.hlsEvents.HLS_STOPPED,
    hlsUrls: null,
    localMicOn: isMicOn,
    localWebcamOn: isWebcamOn,
    localScreenShareOn: isScreenSharing,

    // Actions
    join,
    leave,
    end,
    muteMic,
    unmuteMic,
    toggleMic,
    disableWebcam,
    enableWebcam,
    toggleWebcam,
    changeWebcam,
    enableScreenShare,
    disableScreenShare,
    toggleScreenShare,
    pin,
    unpin,

    // Recording (stubs)
    startRecording: () => console.warn('Recording requires Agora Cloud Recording setup'),
    stopRecording: () => console.warn('Recording requires Agora Cloud Recording setup'),

    // Livestream (stubs)
    startLivestream: () => Promise.resolve(),
    stopLivestream: () => Promise.resolve(),

    // Mode change
    changeMode: (newMode) => {
      console.warn('Mode change not directly supported in Agora');
    },

    // Device methods
    getWebcams: () => AgoraRTC.getCameras(),
    getMics: () => AgoraRTC.getMicrophones(),
    getSpeakers: () => AgoraRTC.getPlaybackDevices(),
    changeMic: async (deviceId) => {
      if (localMicrophoneTrack) {
        await localMicrophoneTrack.setDevice(deviceId);
      }
    },
    changeWebcamDevice: async (deviceId) => {
      if (localCameraTrack && localCameraTrack.setDevice) {
        await localCameraTrack.setDevice(deviceId);
      }
    },
    enableAdaptiveSubscriptions: () => {},
  }), [
    meetingId, localParticipant, participants, presenterId, activeSpeakerId,
    pinnedParticipants, isMicOn, isWebcamOn, isScreenSharing,
    join, leave, end, muteMic, unmuteMic, toggleMic, disableWebcam, enableWebcam,
    toggleWebcam, changeWebcam, enableScreenShare, disableScreenShare, toggleScreenShare,
    pin, unpin, localMicrophoneTrack, localCameraTrack
  ]);

  const contextValue = {
    // Raw Agora objects
    client,
    localAudioTrack: localMicrophoneTrack,
    localVideoTrack: localCameraTrack,
    localScreenTrack,

    // Meeting state
    meetingState,
    meetingId,

    // VideoSDK-compatible interface
    ...mMeeting,

    // Simulcasting
    setRemoteVideoStreamType,

    // Register callbacks
    registerCallbacks: (callbacks) => {
      console.log('[AgoraReactContext] registerCallbacks called');
      callbacksRef.current = { ...callbacksRef.current, ...callbacks };
    },

    // SDK components for direct use
    LocalUser,
    RemoteUser,
    remoteUsers,
  };

  return (
    <AgoraMeetingContext.Provider value={contextValue}>
      {children}
    </AgoraMeetingContext.Provider>
  );
}

/**
 * Main Meeting Provider Component
 * Wraps AgoraRTCProvider with the inner meeting logic
 */
export function AgoraMeetingProvider({
  children,
  config,
  token,
  reinitialiseMeetingOnConfigChange = false,
  joinWithoutUserInteraction = false,
}) {
  console.log('[AgoraMeetingProvider] Rendering with config:', config?.meetingId);

  // Create and memoize the Agora client
  const [client] = useState(() => AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' }));

  return (
    <AgoraRTCProvider client={client}>
      <AgoraMeetingInner
        config={config}
        token={token}
        joinWithoutUserInteraction={joinWithoutUserInteraction}
      >
        {children}
      </AgoraMeetingInner>
    </AgoraRTCProvider>
  );
}

// useMeeting hook - VideoSDK-compatible interface
export function useAgoraMeeting(callbacks = {}) {
  const context = useContext(AgoraMeetingContext);
  if (!context) {
    throw new Error('useAgoraMeeting must be used within AgoraMeetingProvider');
  }

  useEffect(() => {
    if (callbacks && Object.keys(callbacks).length > 0) {
      context.registerCallbacks(callbacks);
    }
  }, []);

  return context;
}

// useParticipant hook - VideoSDK-compatible interface
export function useAgoraParticipant(participantId, callbacks = {}) {
  const context = useContext(AgoraMeetingContext);
  const participants = context?.participants;
  const setRemoteVideoStreamType = context?.setRemoteVideoStreamType;

  let participantData = participants?.get(participantId);

  // Try to find by string version
  if (!participantData && participantId) {
    participantData = participants?.get(String(participantId));
  }

  // Check if matches local participant's numeric UID
  if (!participantData && participantId && context?.localParticipant) {
    const localParticipant = context.localParticipant;
    const localNumericUid = stringToNumericUid(localParticipant?.id);
    if (String(participantId) === String(localNumericUid)) {
      participantData = localParticipant;
    }
  }

  const defaultReturn = {
    webcamStream: null,
    micStream: null,
    screenShareStream: null,
    webcamOn: false,
    micOn: false,
    screenShareOn: false,
    isLocal: false,
    displayName: '',
    isActiveSpeaker: false,
    pinState: { cam: false, share: false },
    mode: AgoraConstants.modes.SEND_AND_RECV,
    participant: null,
    metaData: {},
    setQuality: () => {},
    enableWebcam: () => {},
    disableWebcam: () => {},
    enableMic: () => {},
    disableMic: () => {},
    pin: () => {},
    unpin: () => {},
    getVideoStats: async () => ([]),
    getAudioStats: async () => ([]),
    getShareStats: async () => ([]),
    getShareAudioStats: async () => ([]),
    consumeWebcamStreams: () => {},
    stopConsumingWebcamStreams: () => {},
  };

  if (!participantData) {
    return defaultReturn;
  }

  const participantObj = {
    id: participantData.id,
    displayName: participantData.displayName,
    metaData: participantData.metaData || {},
  };

  return {
    ...participantData,
    participant: participantObj,
    pinState: participantData.pinState ? { cam: true, share: false } : { cam: false, share: false },
    isActiveSpeaker: context?.activeSpeakerId === participantId,
    setQuality: (quality) => {
      if (!participantData.isLocal && setRemoteVideoStreamType) {
        const streamType = quality === 'high' ? 0 : 1;
        setRemoteVideoStreamType(participantId, streamType);
      }
    },
    getVideoStats: async () => ([]),
    getAudioStats: async () => ([]),
    getShareStats: async () => ([]),
    getShareAudioStats: async () => ([]),
    consumeWebcamStreams: () => {},
    stopConsumingWebcamStreams: () => {},
  };
}

// useMediaDevice hook - VideoSDK-compatible interface
export function useAgoraMediaDevice(options = {}) {
  const { onDeviceChanged } = options;

  useEffect(() => {
    if (onDeviceChanged) {
      AgoraRTC.onMicrophoneChanged = onDeviceChanged;
      AgoraRTC.onCameraChanged = onDeviceChanged;
      AgoraRTC.onPlaybackDeviceChanged = onDeviceChanged;
    }

    return () => {
      AgoraRTC.onMicrophoneChanged = null;
      AgoraRTC.onCameraChanged = null;
      AgoraRTC.onPlaybackDeviceChanged = null;
    };
  }, [onDeviceChanged]);

  return {
    getMicrophones: AgoraRTC.getMicrophones,
    getCameras: AgoraRTC.getCameras,
    getPlaybackDevices: AgoraRTC.getPlaybackDevices,
    requestPermission: async (type) => {
      const resultMap = new Map();
      try {
        if (type === 'audio') {
          const track = await AgoraRTC.createMicrophoneAudioTrack();
          track.close();
          resultMap.set('audio', true);
        } else if (type === 'video') {
          const track = await AgoraRTC.createCameraVideoTrack();
          track.close();
          resultMap.set('video', true);
        }
      } catch (error) {
        console.error(`Failed to get ${type} permission:`, error);
        resultMap.set(type, false);
      }
      return resultMap;
    },
    checkPermissions: async () => {
      const resultMap = new Map();
      resultMap.set('audio', false);
      resultMap.set('video', false);

      try {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        audioTrack.close();
        resultMap.set('audio', true);
      } catch (error) {
        console.log('Audio permission not available:', error.message);
      }

      try {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        videoTrack.close();
        resultMap.set('video', true);
      } catch (error) {
        console.log('Video permission not available:', error.message);
      }

      return resultMap;
    },
  };
}

// Export Constants for compatibility
export const Constants = AgoraConstants;

// Export SDK components for direct use
export { LocalUser, RemoteUser, AgoraRTCProvider };

export default AgoraMeetingContext;
